/**
 * DEV ONLY — the send simulation: is it on (never asked in a production
 * build), fake sends for a group, the next-day step (computed server-side
 * in the recipient timezone) and Clear. Every act toasts its outcome, so a
 * refused or empty simulation never passes silently.
 * For Media Devoted | Created By Ciro Autiero
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getApiErrorMessage } from '@/lib/api-error';
import { useToast } from '@/providers/ToastProvider';
import { api } from '@/services/api';

import { dropGeneratorKeys } from '../useDropGenerator';

const BASE = '/drop-generator/verdicts';

export type SimPreset = 'healthy' | 'terrible';

export interface SimulateInput {
  countryKey: string;
  containerId: string;
  listGroupId?: string;
  drops?: number;
  preset: SimPreset;
  sentMinutesAgo?: number;
  at?: string;
}

export function useSimulationAvailable(countryKey: string | null) {
  return useQuery({
    queryKey: [...dropGeneratorKeys.all, 'simulation', countryKey ?? 'none'],
    queryFn: async () => {
      const res = await api.get<{ data: { enabled: boolean } }>(
        `${BASE}/${countryKey}/simulate`,
      );
      return res.data.data.enabled;
    },
    enabled: Boolean(countryKey) && import.meta.env.DEV,
    staleTime: Infinity,
    retry: false,
  });
}

export function useSimulation() {
  const queryClient = useQueryClient();
  const { success, error } = useToast();
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: dropGeneratorKeys.all });
  const fail = (title: string) => (err: unknown) =>
    error(title, getApiErrorMessage(err));

  const simulateSends = useMutation({
    mutationFn: async ({ countryKey, ...body }: SimulateInput) => {
      const res = await api.post<{ data: { simulated: number } }>(
        `${BASE}/${countryKey}/simulate`,
        body,
      );
      return res.data.data;
    },
    onSuccess: ({ simulated }) => {
      invalidate();
      success(
        `${simulated} fake send${simulated === 1 ? '' : 's'} stamped`,
        'Nothing went to SMSWiser — press Evaluate verdicts to watch the judge act.',
      );
    },
    onError: fail('Simulation refused'),
  });

  const nextDay = useMutation({
    mutationFn: async (input: { countryKey: string; at: string | null }) => {
      const res = await api.post<{ data: { at: string; runDate: string } }>(
        `${BASE}/${input.countryKey}/simulate/next-day`,
        input.at ? { at: input.at } : {},
      );
      return res.data.data;
    },
    onSuccess: invalidate,
    onError: fail('Next day not generated'),
  });

  const clearSimulation = useMutation({
    mutationFn: async (countryKey: string) => {
      const res = await api.post<{
        data: { unsent: number; futureRuns: number; keptRuns: number };
      }>(`${BASE}/${countryKey}/simulate/clear`);
      return res.data.data;
    },
    onSuccess: ({ unsent, futureRuns, keptRuns }) => {
      invalidate();
      success(
        'Simulation cleared',
        `${unsent} fake send(s) forgotten · ${futureRuns} simulated day(s) removed${keptRuns > 0 ? ` · ${keptRuns} kept (they hold real drops)` : ''}`,
      );
    },
    onError: fail('Clear simulation failed'),
  });

  return { simulateSends, nextDay, clearSimulation };
}
